import React, { useState, useEffect } from "react";
import "./LandingGames.css";

const GAMES = [
  { id: "pacman",  title: "PAC-MAN",  desc: "Eat the dots, dodge the ghosts.", url: "/pacman.html",  icon: "ᗧ", color: "yellow" },
  { id: "invader", title: "INVADER",  desc: "Defend Earth from the invaders.",  url: "/invader.html", icon: "👾", color: "green"  },
  { id: "snake",   title: "SNAKE",    desc: "Grow long, don't bite yourself.",   url: "/snake.html",   icon: "🐍", color: "cyan"   },
];

const PSEUDO_RE = /^[a-zA-Z0-9_]{1,10}$/;

function validatePseudo(v) {
  if (!v.trim()) return "Enter your username to play.";
  if (v.length > 10) return "Max 10 characters.";
  if (!PSEUDO_RE.test(v)) return "Letters, digits and _ only.";
  return null;
}

function GlobalStats() {
  const [stats, setStats] = useState(null);

  useEffect(() => {
    fetch("/api/fast/games/global-stats")
      .then((r) => r.json())
      .then((d) => setStats(d))
      .catch(() => setStats({ nb_trump: 0, nb_putin: 0, top10: [] }));
  }, []);

  if (!stats) return <p className="lg-lb-loading">Loading…</p>;

  return (
    <div className="lg-global">
      <div className="lg-global-counters">
        <div className="lg-global-counter lg-global-counter--trump">
          <img src="/images/invader/trump-octopus.webp" alt="Trump" className="lg-global-img" />
          <span className="lg-global-count">{stats.nb_trump.toLocaleString()}</span>
          <span className="lg-global-label">TRUMP DESTROYED</span>
        </div>
        <div className="lg-global-counter lg-global-counter--putin">
          <img src="/images/invader/putin-tank.webp" alt="Putin" className="lg-global-img" />
          <span className="lg-global-count">{stats.nb_putin.toLocaleString()}</span>
          <span className="lg-global-label">PUTIN DESTROYED</span>
        </div>
      </div>

      {stats.top10.length > 0 && (
        <ol className="lg-lb-list">
          {stats.top10.map((e, i) => (
            <li key={i} className={`lg-lb-row lg-lb-row--red ${i === 0 ? "lg-lb-row--first" : ""}`}>
              <span className="lg-lb-rank">{i === 0 ? "▶" : `${i + 1}`}</span>
              <span className="lg-lb-pseudo">{e.pseudo}</span>
              <span className="lg-global-kills">
                <span className="lg-global-kills-trump">{e.nb_trump} 🐙</span>
                <span className="lg-global-kills-putin">{e.nb_putin} 🪖</span>
              </span>
            </li>
          ))}
        </ol>
      )}
      {stats.top10.length === 0 && <p className="lg-lb-empty">No kills yet. Be the first!</p>}
    </div>
  );
}

function LeaderboardTable({ game, color }) {
  const [entries, setEntries] = useState(null);

  useEffect(() => {
    fetch(`/api/fast/games/leaderboard/${game}`)
      .then((r) => r.json())
      .then((d) => setEntries(d.entries || []))
      .catch(() => setEntries([]));
  }, [game]);

  if (entries === null) return <p className="lg-lb-loading">Loading…</p>;
  if (entries.length === 0) return <p className="lg-lb-empty">No scores yet. Be the first!</p>;

  return (
    <ol className="lg-lb-list">
      {entries.map((e, i) => (
        <li key={i} className={`lg-lb-row lg-lb-row--${color} ${i === 0 ? "lg-lb-row--first" : ""}`}>
          <span className="lg-lb-rank">{i === 0 ? "▶" : `${i + 1}`}</span>
          <span className="lg-lb-pseudo">{e.pseudo}</span>
          <span className="lg-lb-score">{e.score.toLocaleString()}</span>
        </li>
      ))}
    </ol>
  );
}

const PSEUDO_STORAGE_KEY = "arcade_pseudo";

export default function LandingGames() {
  const [pseudo, setPseudo] = useState(() => localStorage.getItem(PSEUDO_STORAGE_KEY) || "");
  const [error, setError]   = useState(null);

  function handlePseudoChange(e) {
    // Reject special chars on input
    const val = e.target.value.replace(/[^a-zA-Z0-9_]/g, "").slice(0, 10);
    setPseudo(val);
    localStorage.setItem(PSEUDO_STORAGE_KEY, val);
    setError(null);
  }

  function launch(url) {
    const err = validatePseudo(pseudo);
    if (err) { setError(err); return; }
    window.location.href = `${url}?pseudo=${encodeURIComponent(pseudo.trim())}`;
  }

  return (
    <div className="lg-page">
      <div className="lg-scanlines" aria-hidden="true" />

      <header className="lg-header">
        <div className="lg-logo-row" aria-hidden="true">
          <span className="lg-logo-dot" />
          <span className="lg-logo-dot" />
          <span className="lg-logo-dot" />
        </div>
        <h1 className="lg-title">
          <span className="lg-title-arcade">ARCADE</span>
          <span className="lg-title-zone"> ZONE</span>
        </h1>
        <p className="lg-subtitle">Pick your game. Enter the arena.</p>
      </header>

      <main className="lg-main">
        {/* ── Pseudo ── */}
        <div className="lg-pseudo-wrap">
          <label className="lg-pseudo-label" htmlFor="lg-pseudo">
            <span className="lg-arrow">▶</span> YOUR NAME
          </label>
          <input
            id="lg-pseudo"
            className={`lg-pseudo-input${error ? " lg-pseudo-input--error" : ""}`}
            type="text"
            placeholder="PLAYER_1"
            value={pseudo}
            onChange={handlePseudoChange}
            maxLength={10}
            autoComplete="off"
            spellCheck={false}
          />
          {error && <p className="lg-pseudo-error">{error}</p>}
          <p className="lg-pseudo-hint">Max 10 chars · letters, digits, _</p>
        </div>

        {/* ── Cartes jeux ── */}
        <div className="lg-cards">
          {GAMES.map((g) => (
            <button
              key={g.id}
              className={`lg-card lg-card--${g.color}`}
              onClick={() => launch(g.url)}
              aria-label={`Play ${g.title}`}
            >
              <span className="lg-card-topbar" aria-hidden="true" />
              <span className="lg-card-icon" aria-hidden="true">{g.icon}</span>
              <div className="lg-card-body">
                <h2 className="lg-card-title">{g.title}</h2>
                <p className="lg-card-desc">{g.desc}</p>
              </div>
              <div className="lg-card-cta">
                <span>PLAY</span>
                <span className="lg-blink">▶</span>
              </div>
            </button>
          ))}
        </div>

        {/* ── Score global ── */}
        <div className="lg-lb lg-lb--red lg-lb--global">
          <div className="lg-lb-header">
            <span className="lg-lb-icon">💀</span>
            <h3 className="lg-lb-title">GLOBAL KILLS</h3>
            <span className="lg-lb-badge">ALL GAMES</span>
          </div>
          <GlobalStats />
        </div>

        {/* ── Leaderboards ── */}
        <div className="lg-leaderboards">
          {GAMES.map((g) => (
            <div key={g.id} className={`lg-lb lg-lb--${g.color}`}>
              <div className="lg-lb-header">
                <span className="lg-lb-icon">{g.icon}</span>
                <h3 className="lg-lb-title">{g.title}</h3>
                <span className="lg-lb-badge">TOP 10</span>
              </div>
              <LeaderboardTable game={g.id} color={g.color} />
            </div>
          ))}
        </div>
      </main>

      <footer className="lg-footer">
        <span className="lg-insert-coin lg-blink">▪ INSERT COIN ▪</span>
      </footer>
    </div>
  );
}
